"use client";

import { useEffect, useRef, useState } from "react";
import { IconGlobe } from "./icons";
import { useNavigationReload } from "./NavigationPending";
import { COOKIE, LANG_NAME, OFFERED, t, type Lang, type Offered } from "@/lib/i18n/index";

/**
 * Sets the cookie and loads the page again: the page ships one language
 * (BLUEPRINT §5), so switching is a round trip, not a show/hide. A full
 * load rather than a router refresh — the language is on <html lang> in
 * the root layout, and everything the shell keeps in memory (theme, fold,
 * the panel's wishes) is on <html> or in localStorage and comes back.
 *
 * A menu rather than a pair of buttons, as in the Mist shell; Escape and a
 * click outside close it. The agent panel's own Escape handler stands back
 * while this is open (it checks for aria-expanded="true").
 */
export function LangToggle({ lang }: { lang: Lang }) {
  const s = t(lang);
  const beginReload = useNavigationReload();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function key(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("click", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  function choose(next: Offered) {
    setOpen(false);
    if (next === lang) return;
    if (!beginReload()) return;
    document.cookie = `${COOKIE}=${next}; path=/; max-age=31536000; samesite=lax`;
    window.location.reload();
  }

  return (
    <div className={"lang" + (open ? " open" : "")} ref={wrap}>
      <button
        type="button"
        className="icon-btn"
        aria-haspopup="listbox"
        data-track="shell.lang_open"
        aria-expanded={open}
        aria-label={s["lang.label"]}
        title={s["lang.label"]}
        onClick={() => setOpen((o) => !o)}
      >
        <IconGlobe />
      </button>
      <div className="lang-menu" role="listbox" aria-label={s["lang.label"]}>
        {OFFERED.map((l) => (
          <button key={l} type="button" role="option" aria-checked={l === lang} data-track="shell.lang" onClick={() => choose(l)}>
            {LANG_NAME[l]}
          </button>
        ))}
      </div>
    </div>
  );
}
